import React, { useState, useEffect } from "react";
import { apiAvailability } from "../api/availability";

const PLACEHOLDER_COLORS = [
  "#3b82f6",
  "#22c55e",
  "#a855f7",
  "#f97316",
  "#14b8a6",
  "#ec4899",
  "#6366f1",
  "#f87171",
];

function getInitials(name) {
  if (!name) {return "?";}
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {return parts[0][0].toUpperCase();}
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getColor(id) {
  return PLACEHOLDER_COLORS[id % PLACEHOLDER_COLORS.length];
}

function StateBadge({ state }) {
  return (
    <span className="tw-inline-block tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-semibold tw-bg-blue-100 tw-text-blue-800">
      {state}
    </span>
  );
}

function SchedulerCard({ scheduler }) {
  return (
    <div className="tw-bg-white tw-rounded-lg tw-shadow tw-p-4 hover:tw-shadow-md tw-transition-shadow">
      <div className="tw-flex tw-items-center tw-gap-3 tw-mb-3">
        <div
          className="tw-w-12 tw-h-12 tw-rounded-full tw-text-white tw-flex tw-items-center tw-justify-center tw-font-bold tw-text-sm tw-flex-shrink-0"
          style={{ backgroundColor: getColor(scheduler.id) }}
        >
          {getInitials(scheduler.name)}
        </div>
        <div>
          <div className="tw-font-semibold tw-text-gray-800 tw-truncate">
            {scheduler.name}
          </div>
          <div className="tw-flex tw-gap-1 tw-flex-wrap tw-mt-1">
            {(scheduler.states || []).map((s) => 
              <StateBadge key={s} state={s} />
            )}
          </div>
        </div>
      </div>
      <div className="tw-flex tw-gap-2 tw-mt-3">
        <a
          href={`/inspector-availability?states=${(scheduler.states || []).join(",")}`}
          className="tw-flex-1 tw-text-center tw-px-3 tw-py-1.5 tw-rounded tw-text-sm tw-font-medium tw-bg-indigo-50 tw-text-indigo-700 hover:tw-bg-indigo-100 tw-transition-colors tw-no-underline"
        >
          <i className="fas fa-calendar-alt tw-mr-1" />
          Availability
        </a>
        <a
          href={`/planning?states=${(scheduler.states || []).join(",")}`}
          className="tw-flex-1 tw-text-center tw-px-3 tw-py-1.5 tw-rounded tw-text-sm tw-font-medium tw-bg-green-50 tw-text-green-700 hover:tw-bg-green-100 tw-transition-colors tw-no-underline"
        >
          <i className="fas fa-route tw-mr-1" />
          Planning
        </a>
      </div>
    </div>
  );
}

const SchedulerOverview = () => {
  const [schedulers, setSchedulers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiAvailability.getSchedulers();
        if (response.data.status === 200) {
          setSchedulers(response.data.result || []);
        } else {
          setError("Failed to load schedulers");
        }
      } catch (err) {
        setError("Failed to load schedulers");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-py-20">
        <div className="tw-text-lg tw-text-gray-500">Loading schedulers...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-py-20">
        <div className="tw-text-lg tw-text-red-500">{error}</div>
      </div>
    );
  }

  if (schedulers.length === 0) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-py-20">
        <div className="tw-text-lg tw-text-gray-500">No schedulers found</div>
      </div>
    );
  }

  return (
    <div className="tw-p-6">
      <h1 className="tw-text-2xl tw-font-bold tw-text-gray-800 tw-mb-6">
        Schedulers
      </h1>
      <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-4 tw-gap-4">
        {schedulers.map((scheduler) => 
          <SchedulerCard key={scheduler.id} scheduler={scheduler} />
        )}
      </div>
    </div>
  );
};

export default SchedulerOverview;
